/**
 * CR-6243: which branding logo to show, given the active theme.
 *
 * The Branding admin has always offered a "Company Dark Logo", stored it and
 * served it — but nothing rendered it, so every surface showed the LIGHT logo
 * even in dark mode, which is the one situation a dark logo exists for. The
 * rule lives here rather than in each of the five logo sites so they cannot
 * drift apart.
 *
 * Both providers sit above the router (ThemeProvider wraps BrandingProvider in
 * AppProviders), so this is safe on the pre-auth pages too — a returning user's
 * saved dark preference applies on the sign-in page.
 *
 * @returns signInLogo - themed company logo for the auth pages, null if none
 * @returns appLogo - logo for the in-app header: the dedicated header logo
 *          when set, otherwise the themed company logo
 * @returns isDark - the active theme, for callers that also swap other art
 *
 * @example
 * const { signInLogo } = useThemedLogo();
 * return signInLogo ? <img src={signInLogo} alt={branding.companyName} /> : <Fallback />;
 */
import { useTheme } from '../contexts/ThemeContext';
import { useBranding } from '../contexts/BrandingContext';

export interface ThemedLogos {
  signInLogo: string | null;
  appLogo: string | null;
  isDark: boolean;
}

export function useThemedLogo(): ThemedLogos {
  const { theme } = useTheme();
  const { branding } = useBranding();

  const isDark = theme === 'dark';
  // Dark variant only when one has actually been uploaded — an install with
  // just a light logo keeps showing it rather than losing its branding.
  const signInLogo = (isDark ? branding.companyDarkLogo : null) || branding.companyLogo || null;
  // The header logo is a single image chosen for a 64px bar and has no dark
  // variant of its own, so it wins outright where it is set.
  const appLogo = branding.headerLogo || signInLogo;

  return { signInLogo, appLogo, isDark };
}
